<template>
  <DynamicsForm
    ref="langModelSelectRef"
    :model="formModel"
    :setting="baseSetting"
    :group-collapse="false"
    layout="labelTop"
    tips-placement="left"
    class="w-[100%]"
    tips-icon="USolidHelpCircle"
  >
    <template #emb_model>
      <div>
        <u-select
          v-model="modelProxy"
          :disabled="!!robotId || modelDisabled"
          :placeholder="
            $t(
              'views.botSetting.BotForm.components.BaseForm.ca2b34232ec279d65a6370a41b2921cd'
            )
          "
          dropdown-menu-class="menu-list"
        >
          <u-option
            v-for="i in embModelList"
            :key="i.model_name"
            :value="i.model_name"
          >
            {{ i.display_name }}
            <span v-if="i.topping" class="tips-text">{{
              $t(
                'views.botSetting.BotForm.components.AdvanceForm.c62e7305c95c241cfbaa4060beffe588'
              )
            }}</span>
          </u-option>
        </u-select>
      </div>
    </template>
  </DynamicsForm>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, computed, watch } from 'vue';
import { DynamicsForm } from '@koi-design/vix-components';
import { useRoute } from 'vue-router';
import { getEmbeddingModelListApi } from '@/api/robot';
import { applyAiWriteEmbeddingModelResolve } from '@/libs/resolveModelReplacement';
import { useStore } from 'vuex';

export default defineComponent({
  name: 'LangModelSelect',
  components: {
    DynamicsForm
  },
  props: {
    langDisabled: {
      type: Boolean,
      default: false
    },
    modelDisabled: {
      type: Boolean,
      default: false
    },
    language: {
      type: String,
      default: ''
    },
    model: {
      type: String,
      default: ''
    },
    resolveUnavailableModel: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submit', 'update:language', 'update:model'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const route = useRoute();
    const robotId = route.query.id;
    const langModelSelectRef = ref(null);
    const formModel = ref({
      emb_language: props.language || '',
      emb_model: props.model || ''
    });

    watch(
      () => formModel.value.emb_language,
      val => {
        emit('update:language', val);
      }
    );
    const modelProxy = computed({
      get: () => formModel.value.emb_model,
      set: val => {
        formModel.value.emb_model = val;
        emit('update:model', val);
      }
    });

    watch(
      () => props.language,
      newVal => {
        if (newVal !== formModel.value.emb_language) {
          formModel.value.emb_language = newVal;
        }
      }
    );
    watch(
      () => props.model,
      newVal => {
        if (newVal !== formModel.value.emb_model) {
          formModel.value.emb_model = newVal;
        }
      }
    );
    const embLangList = ref([]);
    const embModelList = ref([]);
    const store = useStore();
    const homelandInfo = computed(() => {
      return store.getters.homelandInfo;
    });
    const baseSetting = computed(() => [
      {
        label: t(
          'views.botSetting.BotForm.components.AdvanceForm.4690e0c2e831487f46215fad582ebbc6'
        ),
        name: 'emb_language',
        type: 'select',
        disabled: props.langDisabled,
        options: embLangList.value,
        width: 'calc(50% - 10px)',
        tips: t('components.LangModelSelect.ec857b018126996410fef0aef76be962'),
        onChange: async val => {
          const target = embLangList.value.find(item => item.value === val);
          embModelList.value = target.model_list;
          if (!embModelList.value.length) {
            formModel.value.emb_model = '';
            emit('update:model', '');
            return;
          }
          const defaultTemplate = embModelList.value.find(item => item.topping);
          let newModel = '';
          if (defaultTemplate) {
            newModel = defaultTemplate.model_name;
          } else {
            newModel = embModelList.value[0].model_name;
          }
          formModel.value.emb_model = newModel;
          console.log(langModelSelectRef.value);
          try {
            await langModelSelectRef.value.validate();
          } catch (error) {
            console.log(error);
          }
          emit('update:model', newModel);
        },
        props: {
          dropdownMenuClass: 'menu-list',
          placeholder: t(
            'components.LangModelSelect.58efc6c9f88cf5744c305e25e850a69a'
          )
        },
        rules: [
          {
            required: true,
            message: t(
              'components.LangModelSelect.58efc6c9f88cf5744c305e25e850a69a'
            )
          }
        ]
      },
      {
        label: t(
          'views.botSetting.BotForm.components.AdvanceForm.bc121ab7b18a2a3e8ffad7bfc957e79b'
        ),
        name: 'emb_model',
        type: 'slot',
        disabled: props.modelDisabled,
        tips: t('components.LangModelSelect.20002d417dcffaf6188b6cfcf340b7c9'),
        width: 'calc(50% - 10px)',
        rules: [
          {
            required: true,
            message: t(
              'components.LangModelSelect.971864d68e5d83e28a608ec9cef24183'
            )
          }
        ]
      }
    ]);
    const init = async () => {
      const res = await getEmbeddingModelListApi({
        homeland_id: homelandInfo.value.homeland
      });
      embLangList.value = res;
      if (formModel.value.emb_language) {
        embModelList.value =
          embLangList.value.find(i => i.value === formModel.value.emb_language)
            ?.model_list || [];
        if (props.resolveUnavailableModel) {
          const changed = await applyAiWriteEmbeddingModelResolve(
            formModel.value,
            embModelList
          );
          if (changed) {
            emit('update:model', formModel.value.emb_model);
          }
        }
      }
    };
    init();

    const validate = async () => {
      return langModelSelectRef.value.validate();
    };
    return {
      formModel,
      modelProxy,
      baseSetting,
      embLangList,
      embModelList,
      robotId,
      langModelSelectRef,
      validate
    };
  }
});
</script>

<style lang="less">
.tips-text {
  color: var(--theme-color);
  margin-left: 3px;
}
</style>
